function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

(() => {
  const SECTION_ID_CONTAINS = "__multicolumn_mAGm9q";

  function bind() {
    const section = document.querySelector(`.shopify-section[id*="${SECTION_ID_CONTAINS}"]`);
    if (!section) return false;

    const cards = section.querySelectorAll(".multicolumn-card");
    console.log("Voth hover: found cards =", cards.length);

    cards.forEach((card) => {
      if (card.dataset.vothBound === "1") return; // prevent double-bind
      card.dataset.vothBound = "1";

      card.classList.add("voth-liquid-card");

      const setPos = (e) => {
        const r = card.getBoundingClientRect();
        const x = ((e.clientX - r.left) / r.width) * 100;
        const y = ((e.clientY - r.top) / r.height) * 100;
        card.style.setProperty("--mx", `${x}%`);
        card.style.setProperty("--my", `${y}%`);
      };

      card.addEventListener("mouseenter", (e) => {
        setPos(e);
        card.classList.add("is-hovering");
      });

      card.addEventListener("mousemove", setPos);

      card.addEventListener("mouseleave", () => {
        card.classList.remove("is-hovering");
      });
    });

    return true;
  }

  function initWithRetry(tries = 12) {
    if (bind()) return;
    if (tries <= 0) {
      console.warn("Voth hover: section not found:", SECTION_ID_CONTAINS);
      return;
    }
    setTimeout(() => initWithRetry(tries - 1), 250);
  }

  document.addEventListener("DOMContentLoaded", () => initWithRetry());
  document.addEventListener("shopify:section:load", () => initWithRetry());
})();

/* ===== Voth: "Our products" split animation (scroll trigger) ===== */
(() => {
  const TARGET_TEXT = "our products"; // match ignoring case
  const CENTER_TOLERANCE_PX = 80;      // how close to screen center triggers
  const COOLDOWN_MS = 900;            // prevents spam triggering

  function findTargetHeadings() {
    // Rich text headings in Dawn are usually h2/h3 inside .rich-text
    const candidates = document.querySelectorAll(
      ".rich-text h1, .rich-text h2, .rich-text h3, .rich-text__heading"
    );

    return Array.from(candidates).filter(el => {
      const t = (el.textContent || "").trim().toLowerCase();
      return t === TARGET_TEXT;
    });
  }

  function wrapHeading(el) {
    if (el.dataset.vothSplitReady === "1") return;

    const raw = (el.textContent || "").trim();
    const parts = raw.split(/\s+/);
    if (parts.length < 2) return;

    const left = parts[0];
    const right = parts.slice(1).join(" ");

    el.innerHTML = `
      <span class="voth-split-heading">
        <span class="voth-split-left">${left}</span>
        <span class="voth-split-right">${right}</span>
      </span>
    `;

    el.dataset.vothSplitReady = "1";
    el.dataset.vothSplitLast = "0";
  }

  function isNearViewportCenter(el) {
    const r = el.getBoundingClientRect();
    const centerY = r.top + r.height / 2;
    const viewportCenter = window.innerHeight / 2;
    return Math.abs(centerY - viewportCenter) <= CENTER_TOLERANCE_PX;
  }

  function triggerOnce(el) {
    const now = Date.now();
    const last = parseInt(el.dataset.vothSplitLast || "0", 10);

    if (now - last < COOLDOWN_MS) return;
    if (!isNearViewportCenter(el)) return;

    el.dataset.vothSplitLast = String(now);

    const wrapper = el.querySelector(".voth-split-heading");
    if (!wrapper) return;

    wrapper.classList.add("is-splitting");
    setTimeout(() => wrapper.classList.remove("is-splitting"), 520);
  }

  function init() {
    const headings = findTargetHeadings();
    if (!headings.length) return;

    headings.forEach(h => wrapHeading(h));

    // Observe visibility; we still validate "near center" ourselves
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        triggerOnce(entry.target);
      });
    }, { threshold: [0.25, 0.5, 0.75] });

    headings.forEach(h => io.observe(h));

    // Also check on scroll (for precise center hit)
    const onScroll = () => headings.forEach(h => triggerOnce(h));
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    // initial run
    onScroll();
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
})();

/* ===== Voth: Our products paragraph "sunrise" box (center trigger ONCE + STAYS) ===== */
(() => {
  const SECTION_ID = "template--27169583628665__rich_text_yRK6U8";
  const CENTER_TOLERANCE_PX = 160; // ggf. höher setzen (200-260), wenn's zu "streng" ist

  function isNearViewportCenter(el) {
    const r = el.getBoundingClientRect();
    const centerY = r.top + r.height / 2;
    const viewportCenter = window.innerHeight / 2;
    return Math.abs(centerY - viewportCenter) <= CENTER_TOLERANCE_PX;
  }

  function init() {
    const section =
  document.getElementById(`shopify-section-${SECTION_ID}`) ||
  document.querySelector('[data-vc-signals-processed="true"]') ||
  document.querySelector('.vc-signal-list')?.closest('.shopify-section');
    if (!section) return console.warn("[VothSunrise] Section not found:", SECTION_ID);

    const heading = section.querySelector("h1, h2, h3, .rich-text__heading");
    if (!heading) return console.warn("[VothSunrise] Heading not found in:", SECTION_ID);

    const p = section.querySelector("p");
    if (!p) return console.warn("[VothSunrise] Paragraph not found in:", SECTION_ID);

    // Wrap paragraph only once
    let box = p.closest(".voth-sunbox");
    if (!box) {
      box = document.createElement("div");
      box.className = "voth-sunbox";
      p.parentNode.insertBefore(box, p);
      box.appendChild(p);
    }

    let activated = false;

    const update = () => {
      if (activated) return;

      if (isNearViewportCenter(heading)) {
        activated = true;
        box.classList.add("is-active");

        // Stop listening once activated (so it never "disappears")
        window.removeEventListener("scroll", update);
        window.removeEventListener("resize", update);
      }
    };

    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);

    update();
    console.log("[VothSunrise] Installed (once+stay) on:", SECTION_ID);
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
})();

/* ===== Voth: Add hover-glow class to the OUTER Free framework card ===== */
(() => {
  const SECTION_ID = "template--27169583628665__rich_text_ph78JW";

  function init() {
    const section = document.getElementById(`shopify-section-${SECTION_ID}`);
    if (!section) return;

    // Try to grab the OUTER card container (different Dawn markups)
    const outer =
      section.querySelector(".rich-text__wrapper") ||
      section.querySelector(".rich-text") ||
      section.querySelector(".page-width");

    if (!outer) return;

    outer.classList.add("voth-product-card");
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
})();

/* ===== Voth: Our products paragraph "sunrise" box (center trigger ONCE + STAYS) ===== */
(() => {
  const SECTION_ID = "template--27169583628665__rich_text_yRK6U8";
  const CENTER_TOLERANCE_PX = 160;

  function isNearViewportCenter(el) {
    const r = el.getBoundingClientRect();
    const centerY = r.top + r.height / 2;
    const viewportCenter = window.innerHeight / 2;
    return Math.abs(centerY - viewportCenter) <= CENTER_TOLERANCE_PX;
  }

  function init() {
    const section = document.getElementById(`shopify-section-${SECTION_ID}`);
    if (!section) return;

    const heading = section.querySelector("h1, h2, h3, .rich-text__heading");
    const p = section.querySelector("p");
    if (!heading || !p) return;

    let box = p.closest(".voth-sunbox");
    if (!box) {
      box = document.createElement("div");
      box.className = "voth-sunbox";
      p.parentNode.insertBefore(box, p);
      box.appendChild(p);
    }

    let activated = false;

    const update = () => {
      if (activated) return;
      if (isNearViewportCenter(heading)) {
        activated = true;
        box.classList.add("is-active");
        window.removeEventListener("scroll", update);
        window.removeEventListener("resize", update);
      }
    };

    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    update();
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
})();

/* ===== Voth: "Our products" split animation (scroll trigger) ===== */
(() => {
  const TARGET_TEXT = "our products";
  const CENTER_TOLERANCE_PX = 80;
  const COOLDOWN_MS = 900;

  function findTargetHeadings() {
    const candidates = document.querySelectorAll(
      ".rich-text h1, .rich-text h2, .rich-text h3, .rich-text__heading"
    );

    return Array.from(candidates).filter(el => {
      const t = (el.textContent || "").trim().toLowerCase();
      return t === TARGET_TEXT;
    });
  }

  function wrapHeading(el) {
    if (el.dataset.vothSplitReady === "1") return;

    const raw = (el.textContent || "").trim();
    const parts = raw.split(/\s+/);
    if (parts.length < 2) return;

    const left = parts[0];
    const right = parts.slice(1).join(" ");

    el.innerHTML = `
      <span class="voth-split-heading">
        <span class="voth-split-left">${left}</span>
        <span class="voth-split-right">${right}</span>
      </span>
    `;

    el.dataset.vothSplitReady = "1";
    el.dataset.vothSplitLast = "0";
  }

  function isNearViewportCenter(el) {
    const r = el.getBoundingClientRect();
    const centerY = r.top + r.height / 2;
    const viewportCenter = window.innerHeight / 2;
    return Math.abs(centerY - viewportCenter) <= CENTER_TOLERANCE_PX;
  }

  function triggerOnce(el) {
    const now = Date.now();
    const last = parseInt(el.dataset.vothSplitLast || "0", 10);

    if (now - last < COOLDOWN_MS) return;
    if (!isNearViewportCenter(el)) return;

    el.dataset.vothSplitLast = String(now);

    const wrapper = el.querySelector(".voth-split-heading");
    if (!wrapper) return;

    wrapper.classList.add("is-splitting");
    setTimeout(() => wrapper.classList.remove("is-splitting"), 520);
  }

  function init() {
    const headings = findTargetHeadings();
    if (!headings.length) return;

    headings.forEach(wrapHeading);

    const onScroll = () => headings.forEach(triggerOnce);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
})();


(() => {
  const SECTION_ID = "template--27169583628665__rich_text_ph78JW";
  const HEADING_BLOCK_ID = "heading_CJDkyt";

  function init() {
    const section = document.getElementById(`shopify-section-${SECTION_ID}`);
    if (!section) return;

    const heading =
      section.querySelector(`[data-shopify-editor-block*="${HEADING_BLOCK_ID}"] .rich-text__heading`) ||
      section.querySelector(".rich-text__heading");

    if (!heading) return;
    if (heading.querySelector(".voth-hgrow")) return;

    const html = heading.innerHTML.trim();
    heading.innerHTML = `<span class="voth-hgrow">${html}</span>`;
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
})();

/* ===============================
   VothCapital Persistent Glow
================================= */

document.addEventListener("DOMContentLoaded", () => {
  const section = document.querySelector(
    "#shopify-section-template--27169583628665__rich_text_WMKyHH"
  );
  if (!section) return;

  const blocks = section.querySelector(".rich-text__blocks");
  if (!blocks) return;

  const LINES_FINISH_MS = 1500; // adjust if needed

  let glowLocked = false;
  let timer = null;

  blocks.addEventListener("mouseenter", () => {
    if (glowLocked) return;

    clearTimeout(timer);
    timer = setTimeout(() => {
      section.classList.add("vc-glow-on");
      glowLocked = true; // stays until refresh
    }, LINES_FINISH_MS);
  });

  blocks.addEventListener("mouseleave", () => {
    if (glowLocked) return;
    clearTimeout(timer);
  });
});


(function () {
  const SECTION_ID = 'shopify-section-template--27169583628665__rich_text_WMKyHH';
  const TOTAL_MS = 1800; // full loop duration (out->in->out) + buffer

  function initOnceLines() {
    const section = document.getElementById(SECTION_ID);
    if (!section) return;

    const blocks = section.querySelector('.rich-text__blocks');
    if (!blocks) return;

    // Prevent double-binding
    if (blocks.dataset.vcOnceBound === '1') return;
    blocks.dataset.vcOnceBound = '1';

    let armed = true;

    blocks.addEventListener('mouseenter', () => {
      if (!armed) return;
      armed = false;

      // start animation and keep it running even if mouse leaves
      section.classList.add('vc-lines-playing');

      window.setTimeout(() => {
        // stop showing lines after the full loop completes
        section.classList.remove('vc-lines-playing');

        // lock everything in
        section.classList.add('vc-lines-used');
        section.classList.add('vc-glow-on');

        requestAnimationFrame(() => {
          section.classList.add('vc-paragraph-on');
        });
      }, TOTAL_MS);
    });
  }

  document.addEventListener('DOMContentLoaded', initOnceLines);
  document.addEventListener('shopify:section:load', initOnceLines);
})();




document.addEventListener("DOMContentLoaded", () => {
  const section = document.querySelector(
    "#shopify-section-template--27169583628665__rich_text_qkDz9r"
  );
  if (!section) return;

  const cards = section.querySelectorAll(".rich-text__blocks > .rich-text__text");
  if (!cards.length) return;

  cards.forEach((card) => {
    card.addEventListener("click", () => {
      const wasOpen = card.classList.contains("is-open");
      cards.forEach((c) => c.classList.remove("is-open"));
      if (!wasOpen) card.classList.add("is-open");
    });
  });
});



document.addEventListener("DOMContentLoaded", function () {
  const section = document.querySelector("#shopify-section-template--27308793823609__main");
  if (!section) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          section.classList.add("vc-active");
          observer.disconnect();
        }
      });
    },
    {
      threshold: 0.35
    }
  );

  observer.observe(section);
});



document.addEventListener("DOMContentLoaded", function () {
  const section = document.querySelector("#shopify-section-template--27308793823609__rich_text_97Ujbw");
  if (!section) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          section.classList.add("vc-inview");
          observer.disconnect();
        }
      });
    },
    {
      threshold: 0.35
    }
  );

  observer.observe(section);
});


document.addEventListener("DOMContentLoaded", () => {
  const wrapper = document.querySelector(".footer .newsletter-form__field-wrapper");
  const input = document.querySelector(".footer .field__input");

  if (!wrapper || !input) return;

  wrapper.addEventListener("click", (e) => {
    if (!wrapper.classList.contains("vc-open")) {
      wrapper.classList.add("vc-open");
      setTimeout(() => input.focus(), 80);
    }
  });

  input.addEventListener("blur", () => {
    if (!input.value.trim()) {
      wrapper.classList.remove("vc-open");
    }
  });
});


const caption = document.querySelector(
  '#shopify-section-template--27309675643257__rich_text_bWcbD4 .rich-text__caption'
);

if (caption) {
  caption.classList.remove('vc-cuts-on');

  setTimeout(() => {
    caption.classList.add('vc-cuts-on');
  }, 900);
}





document.addEventListener('DOMContentLoaded', function () {
  const heading = document.querySelector(
    '#shopify-section-template--27309675643257__rich_text_CxgCwD .rich-text__heading'
  );

  if (!heading) return;

  if (heading.dataset.vcProcessed === 'true') return;
  heading.dataset.vcProcessed = 'true';

  heading.innerHTML = heading.innerHTML.replace(
    'included',
    '<span class="vc-included">included</span>'
  );
});


document.addEventListener('DOMContentLoaded', function () {
  const section = document.querySelector(
    '#shopify-section-template--27309675643257__rich_text_CxgCwD'
  );

  if (!section) return;

  function checkIncluded() {
    const rect = section.getBoundingClientRect();
    const center = rect.top + rect.height / 2;
    const screenCenter = window.innerHeight / 2;

    if (Math.abs(center - screenCenter) < 100) {
      section.classList.add('vc-included-on');
      window.removeEventListener('scroll', checkIncluded);
    }
  }

  window.addEventListener('scroll', checkIncluded);
  checkIncluded();
});



document.addEventListener('DOMContentLoaded', function () {
  const section = document.querySelector(
    '#shopify-section-template--27309675643257__rich_text_CxgCwD'
  );

  if (!section) return;

  function checkIncludedAndSubheader() {
  const rect = section.getBoundingClientRect();
  const center = rect.top + rect.height / 2;
  const screenCenter = window.innerHeight / 2;

  const isLandscapeMobile =
    window.matchMedia("(max-height: 500px) and (orientation: landscape)").matches;

  const triggerDistance = isLandscapeMobile ? 260 : 100;

  if (Math.abs(center - screenCenter) < triggerDistance) {
    section.classList.add('vc-included-on');

    setTimeout(() => {
      section.classList.add('vc-subheader-on');
    }, 1200);

    window.removeEventListener('scroll', checkIncludedAndSubheader);
  }
}

  window.addEventListener('scroll', checkIncludedAndSubheader);
  checkIncludedAndSubheader();
});




document.addEventListener('DOMContentLoaded', function () {
  const section = document.querySelector(
    '#shopify-section-template--27309675643257__rich_text_CxgCwD'
  );

  if (!section) return;

  const signalBlock = section.querySelector(
  '[data-shopify-editor-block*="text_HNUYCq"]'
);

  if (!signalBlock) return;
  if (signalBlock.dataset.vcSignalsProcessed === 'true') return;
  signalBlock.dataset.vcSignalsProcessed = 'true';

  const raw = signalBlock.innerHTML
    .split(/<br\s*\/?>/i)
    .map(line =>
      line
        .replace(/<\/?p>/gi, '')
        .replace(/^\s*•\s*/g, '')
        .trim()
    )
    .filter(Boolean);

  if (!raw.length) return;

  const items = raw
    .map(line => `<div class="vc-signal-item">${line}</div>`)
    .join('');

  signalBlock.innerHTML = `<div class="vc-signal-list">${items}</div>`;

  function checkSignals() {
    const rect = section.getBoundingClientRect();
    const center = rect.top + rect.height / 2;
    const screenCenter = window.innerHeight / 2;

    if (rect.top < window.innerHeight * 0.85 && rect.bottom > window.innerHeight * 0.15) {
      section.classList.add('vc-signals-on');
      section.classList.add('rich-text');
      window.removeEventListener('scroll', checkSignals);
    }
  }

  const signalObserver = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      section.classList.add('vc-signals-on');
      section.classList.add('rich-text');
      signalObserver.disconnect();
    }
  });
}, {
  threshold: 0.35
});
signalObserver.observe(section);
});



document.addEventListener('DOMContentLoaded', function () {
  const section = document.querySelector(
    '#shopify-section-template--27309675643257__rich_text_dwP7RE'
  );

  if (!section) return;

  const heading = section.querySelector(
  '.rich-text__heading[data-shopify-editor-block*="__heading_H9ybMi"], h2.rich-text__heading'
);

  if (!heading) return;
  if (heading.dataset.vcSplitDone === 'true') return;
  heading.dataset.vcSplitDone = 'true';

  const text = heading.textContent.trim();

  // split manually into 2 parts
  const parts = text.split(' to ');

  if (parts.length !== 2) return;

  heading.innerHTML = `
    <span class="vc-split-left">${parts[0]}</span>
    <span class="vc-split-right">to ${parts[1]}</span>
  `;
});


document.addEventListener('DOMContentLoaded', function () {
  const section = document.querySelector(
    '#shopify-section-template--27309675643257__rich_text_dwP7RE'
  );

  if (!section) return;

  function triggerSplit() {
    const rect = section.getBoundingClientRect();
    const center = rect.top + rect.height / 2;
    const screenCenter = window.innerHeight / 2;

    if (Math.abs(center - screenCenter) < 120) {
      section.classList.add('vc-split-on');
      window.removeEventListener('scroll', triggerSplit);
    }
  }

  window.addEventListener('scroll', triggerSplit);
  triggerSplit();
});




document.addEventListener('DOMContentLoaded', function () {
  const heading = document.querySelector(
    '#shopify-section-template--27309675643257__rich_text_bWcbD4 .rich-text__heading'
  );

  if (!heading) return;

  const desktopText = 'Three risk paths. One system.';
  const mobileText = 'Three risk paths.<br>One system.';

  function updateMobileHeading() {
    if (window.innerWidth <= 749) {
      heading.innerHTML = mobileText;
    } else {
      heading.textContent = desktopText;
    }
  }

  updateMobileHeading();
  window.addEventListener('resize', updateMobileHeading);
});


document.querySelectorAll('[data-shopify-editor-block*="__caption_rRqcwx"]').forEach(el => {
  if (!el.querySelector('.vc-lines')) {
    const wrapper = document.createElement('span');
    wrapper.className = 'vc-lines';
    el.appendChild(wrapper);
  }
});



document.addEventListener("DOMContentLoaded", function () {
  const section = document.querySelector(
    "#shopify-section-template--27309675643257__rich_text_LznVH6"
  );
  if (!section) return;

  const leftP = section.querySelector(
    ".rich-text__text[data-shopify-editor-block*='__text_PLyxre'] p"
  );
  const rightP = section.querySelector(
    ".rich-text__text[data-shopify-editor-block*='__text_fn9T6n'] p"
  );

  if (leftP && leftP.dataset.leftParagraphFixed !== "true") {
    leftP.innerHTML =
      "Investors who want clear rules instead of constant opinions<br>" +
      "People who prefer systems over hype and speculation<br>" +
      "Long-term thinkers who stay consistent through market cycles<br>" +
      "Those who want a repeatable system, not stock tips";
    leftP.dataset.leftParagraphFixed = "true";
  }

  if (rightP && rightP.dataset.rightParagraphFixed !== "true") {
    rightP.innerHTML =
      "People looking for quick profits or short-term trades<br>" +
      "Anyone expecting predictions, signals, or “guaranteed” returns<br>" +
      "Investors who can’t tolerate drawdowns or volatility<br>" +
      "Those who prefer reactive decision-making over structure";
    rightP.dataset.rightParagraphFixed = "true";
  }

  const textBlocks = section.querySelectorAll(".rich-text__text p");

  textBlocks.forEach((p) => {
    if (p.dataset.linesWrapped === "true") return;

    const parts = p.innerHTML
      .split(/<br\s*\/?>/i)
      .map((line) => line.trim())
      .filter(Boolean);

    if (parts.length <= 1) return;

    p.innerHTML = parts.map((line) => `<span class="vc-line">${line}</span>`).join("");
    p.dataset.linesWrapped = "true";
  });
});



document.addEventListener("DOMContentLoaded", function () {
  const p = document.querySelector(
  "#shopify-section-template--27309675643257__rich_text_LznVH6 .rich-text__text[data-shopify-editor-block*='__text_PLyxre'] p, #shopify-section-template--27309675643257__rich_text_LznVH6 .rich-text__text:nth-of-type(1) p"
);

  if (!p) return;
  if (p.dataset.leftParagraphFixed === "true") return;

  p.innerHTML =
  '<span class="vc-bullet-line">Investors who want clear rules instead of constant opinions</span>' +
  '<span class="vc-bullet-line">People who prefer systems over hype and speculation</span>' +
  '<span class="vc-bullet-line">Long-term thinkers who stay consistent through market cycles</span>' +
  '<span class="vc-bullet-line">Those who want a repeatable system, not stock tips</span>';

  p.dataset.leftParagraphFixed = "true";
});




document.addEventListener("DOMContentLoaded", function () {
  const p = document.querySelector(
    "#shopify-section-template--27309675643257__rich_text_LznVH6 .rich-text__text[data-shopify-editor-block*='__text_fn9T6n'] p"
  );

  if (!p) return;
  if (p.dataset.rightParagraphFixed === "true") return;

  p.innerHTML =
    "People looking for quick profits or short-term trades<br>" +
    "Anyone expecting predictions, signals, or “guaranteed” returns<br>" +
    "Investors who can’t tolerate drawdowns or volatility<br>" +
    "Those who prefer reactive decision-making over structure";

  p.dataset.rightParagraphFixed = "true";
});



function updateClarityText() {
  const p = document.querySelector(
    "#shopify-section-template--27309675643257__rich_text_dwP7RE .rich-text__text[data-shopify-editor-block*='__text_RXdFwa'] p"
  );

  if (!p) return;

  if (window.innerWidth <= 749) {
    p.innerHTML = "No noise. No promotions.<br>Just clarity.";
  } else {
    p.innerHTML = "No noise. No promotions. Just clarity.";
  }
}

document.addEventListener("DOMContentLoaded", updateClarityText);
window.addEventListener("resize", updateClarityText);




document.addEventListener("DOMContentLoaded", function () {
  const section = document.querySelector(
    "#shopify-section-template--27169583628665__rich_text_qkDz9r"
  );

  const outroSection = document.querySelector(
    "#shopify-section-template--27169583628665__rich_text_779cJG"
  );

  if (!section) return;

  function triggerSequence() {
    const rect = section.getBoundingClientRect();
    const center = rect.top + rect.height / 2;
    const screenCenter = window.innerHeight / 2;
    const triggerRange = window.innerWidth <= 749 ? 260 : 120;

    if (Math.abs(center - screenCenter) < triggerRange) {
      section.classList.add("vc-heading-on");

      setTimeout(() => {
        section.classList.add("vc-rail-on");
      }, 500);

      setTimeout(() => {
        section.classList.add("vc-plates-show");
        section.classList.add("vc-crack-run");

        if (outroSection) {
          const outro = outroSection.querySelector(".vc-gold-outro");
          const spans = outroSection.querySelectorAll(".vc-gold-outro span");

          if (outro) {
            outro.style.opacity = "1";
          }

          spans.forEach((span) => {
            span.style.opacity = "0";
            span.style.transform = "translateY(8px)";
            span.style.transition = "none";
          });

          void outroSection.offsetWidth;

          spans.forEach((span, index) => {
            setTimeout(() => {
              span.style.transition = "opacity .25s ease, transform .25s ease";
              span.style.opacity = "1";
              span.style.transform = "translateY(0)";
            }, index * 30);
          });
        }
      }, 1200);

      window.removeEventListener("scroll", triggerSequence);
    }
  }

  window.addEventListener("scroll", triggerSequence);
  triggerSequence();
});





document.addEventListener("DOMContentLoaded", () => {

  if (window.innerWidth > 749) return;

  const signalText = document.querySelector(
    '#shopify-section-template--27309675643257__rich_text_CxgCwD .rich-text__blocks > .rich-text__text:nth-of-type(2) p'
  );

  if (!signalText) return;

  signalText.innerHTML = signalText.innerHTML
    .replace(/<br\s*\/?>/gi, '<br><br>');

});




document.addEventListener("DOMContentLoaded", () => {

  if (window.innerWidth > 749) return;

  const section = document.querySelector(
    '#shopify-section-template--27169583628665__rich_text_WMKyHH'
  );

  if (!section) return;

  const observer = new IntersectionObserver((entries) => {

    entries.forEach(entry => {

      if (entry.isIntersecting) {
        section.classList.add('vc-lines-playing');

setTimeout(() => {
  section.classList.add('vc-glow-on');
  section.classList.add('vc-paragraph-on');
}, 1800);
      }

    });

  }, {
    threshold: 0.35
  });

  observer.observe(section);

});




document.addEventListener("DOMContentLoaded", () => {
  const proBox = document.querySelector(
    "#shopify-section-template--27169583628665__rich_text_WMKyHH"
  );

  if (!proBox) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        proBox.classList.add("vc-lines-playing");
        setTimeout(() => {
  proBox.classList.add("vc-paragraph-on");
}, 1400);

        observer.unobserve(proBox);
      }
    });
  }, { threshold: 0.25 });

  observer.observe(proBox);
});






/* =========================================================
   VothCapital Membership Page
   Second Main Heading - Gold Brush Reveal Trigger
   Desktop First
========================================================= */

document.addEventListener("DOMContentLoaded", function () {
  const vcSecondHeading = document.querySelector(
    "#shopify-section-template--29218499854713__rich_text_aT93Jw h2.rich-text__heading"
  );

  if (!vcSecondHeading) return;

  const vcBrushObserver = new IntersectionObserver(
    function (entries, observer) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          vcSecondHeading.classList.add("vc-brush-active");
          observer.unobserve(vcSecondHeading);
        }
      });
    },
    {
      root: null,
      threshold: 0,
      rootMargin: "0px 0px -40% 0px"
    }
  );

  vcBrushObserver.observe(vcSecondHeading);
});




/* =========================================================
   VothCapital Membership Page
   "More Than a Membership" Heading Reveal
   Desktop First
========================================================= */

document.addEventListener("DOMContentLoaded", function () {
  const vcLayerHeading = document.querySelector(
    "#shopify-section-template--29218499854713__rich_text_6fk8PC h2.rich-text__heading"
  );

  if (!vcLayerHeading) return;

  vcLayerHeading.innerHTML = `
    <span class="vc-layer-heading-top">More Than a Membership.</span>
    <span class="vc-layer-heading-bottom">Built Around the Decisions That Matter Most</span>
  `;

  const vcLayerObserver = new IntersectionObserver(
    function (entries, observer) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          vcLayerHeading.classList.add("vc-layer-heading-active");
          observer.unobserve(vcLayerHeading);
        }
      });
    },
    {
      threshold: 0,
      rootMargin: "0px 0px -35% 0px"
    }
  );

  vcLayerObserver.observe(vcLayerHeading);
});



/* =========================================================
   VothCapital Membership Page
   Layer Intro Paragraph - Frosted Glass Reveal  under the header 
   "More Than a Membership.Built Around the Decisions That Matter Most"
   Desktop First
========================================================= */

document.addEventListener("DOMContentLoaded", function () {
  const vcLayerGlassText = document.querySelector(
    "#shopify-section-template--29218499854713__rich_text_6fk8PC .rich-text__text"
  );

  if (!vcLayerGlassText) return;

  const vcLayerGlassObserver = new IntersectionObserver(
    function (entries, observer) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          vcLayerGlassText.classList.add("vc-layer-glass-active");
          observer.unobserve(vcLayerGlassText);
        }
      });
    },
    {
      root: null,
      threshold: 0,
      rootMargin: "0px 0px -30% 0px"
    }
  );

  vcLayerGlassObserver.observe(vcLayerGlassText);
});





/* =========================================================
   VothCapital Membership Page
   Foundation / Protection / Evolution Heading Reveal
========================================================= */

document.addEventListener("DOMContentLoaded", function () {
  const vcLayerTitles = document.querySelectorAll(
    "#shopify-section-template--29218499854713__multirow_k3pnfV .image-with-text__heading"
  );

  if (!vcLayerTitles.length) return;

  const vcLayerTitleObserver = new IntersectionObserver(
    function (entries, observer) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add("vc-layer-title-active");
          observer.unobserve(entry.target);
        }
      });
    },
    {
      root: null,
      threshold: 0,
      rootMargin: "0px 0px -45% 0px"
    }
  );

  vcLayerTitles.forEach(function (title) {
    vcLayerTitleObserver.observe(title);
  });
});





/* =========================================================
   VothCapital Membership Page
   Everything You Need Heading - Falling Glow Reveal
========================================================= */

document.addEventListener("DOMContentLoaded", function () {
  const vcFallHeading = document.querySelector(
    "#shopify-section-template--29218499854713__rich_text_QbHwPQ .rich-text__heading"
  );

  if (!vcFallHeading) return;

/* Mobile uses two lines; desktop stays on one line */
if (
  window.matchMedia(
    "(max-width: 480px) and (orientation: portrait), " +
    "(min-width: 800px) and (max-width: 1200px) and (min-height: 800px) and (orientation: portrait), " +
    "(min-width: 640px) and (max-width: 900px) and (max-height: 500px) and (orientation: landscape)"
  ).matches
) {
  vcFallHeading.innerHTML = `
    <span class="vc-fall-line vc-fall-line-one">Everything You Need.</span><br>
    <span class="vc-fall-line vc-fall-line-two">Every Single Month</span>`;
} else {
  vcFallHeading.innerHTML = `
    <span class="vc-fall-line vc-fall-line-one">Everything You Need.</span>
    <span class="vc-fall-line vc-fall-line-two">&nbsp;Every Single Month</span>`;
}

  const vcFallObserver = new IntersectionObserver(
    function (entries, observer) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          vcFallHeading.classList.add("vc-fall-active");

          setTimeout(function () {
  const vcFallParagraph = document.querySelector(
    "#shopify-section-template--29218499854713__rich_text_QbHwPQ .rich-text__text"
  );

  if (vcFallParagraph) {
    vcFallParagraph.classList.add("vc-glass-active");
  }
}, 1300);
          observer.unobserve(vcFallHeading);
        }
      });
    },
    {
      root: null,
      threshold: 0,
      rootMargin: "0px 0px -45% 0px"
    }
  );

  vcFallObserver.observe(vcFallHeading);
});




/* =========================================================
   VothCapital Membership Page
   Monthly Value Cards Reveal
========================================================= */

document.addEventListener("DOMContentLoaded", function () {
  const monthlyCards = document.querySelectorAll(
    "#shopify-section-template--29218499854713__multicolumn_BV6UxX .multicolumn-card"
  );

  if (!monthlyCards.length) return;

  const monthlyObserver = new IntersectionObserver(
    function (entries, observer) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          monthlyCards.forEach(function (card, index) {
            setTimeout(function () {
              card.classList.add("vc-monthly-card-active");
            }, index * 220);
          });

          observer.disconnect();
        }
      });
    },
    {
      root: null,
      threshold: 0,
      rootMargin: "0px 0px -40% 0px"
    }
  );

  monthlyObserver.observe(monthlyCards[0]);
});





/* =========================================================
   Market Never Stops Changing Header
   Split Slide-In + Blue Glow
========================================================= */

document.addEventListener("DOMContentLoaded", function () {
  const marketHeading = document.querySelector(
    "#shopify-section-template--29218499854713__rich_text_Wjx3JA .rich-text__heading"
  );

  if (!marketHeading) return;

  marketHeading.innerHTML = `
    <span class="vc-market-line vc-market-left">Because The Market </span>
    <span class="vc-market-line vc-market-right">Never Stops Changing</span>
  `;

  const marketObserver = new IntersectionObserver(
    function (entries, observer) {
      entries.forEach(function (entry) {
       if (entry.isIntersecting) {
  marketHeading.classList.add("vc-market-active");

  setTimeout(function () {
    const marketParagraph = document.querySelector(
      "#shopify-section-template--29218499854713__rich_text_Wjx3JA .rich-text__text"
    );

    if (marketParagraph) {
      marketParagraph.classList.add("vc-market-text-active");
    }
  }, 1300);

  observer.unobserve(marketHeading);
}
      });
    },
    {
      root: null,
      threshold: 0,
      rootMargin: "0px 0px -45% 0px"
    }
  );

  marketObserver.observe(marketHeading);
});




/* ==========================================================
   VothCapital Membership Page
   Monthly Briefing Rows – Golden Breathing Heading Reveal
   ========================================================== */

document.addEventListener("DOMContentLoaded", function () {
  const vcGoldHeadings = document.querySelectorAll(
    "#shopify-section-template--29218499854713__image_with_text_TG6E78 .image-with-text__heading," +
    "#shopify-section-template--29218499854713__image_with_text_9kweqj .image-with-text__heading," +
    "#shopify-section-template--29218499854713__image_with_text_py6bhE .image-with-text__heading"
  );

  if (!vcGoldHeadings.length) return;

  const vcGoldHeadingObserver = new IntersectionObserver(
    function (entries, observer) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        entry.target.classList.add("vc-gold-breathe-active");
        observer.unobserve(entry.target);
      });
    },
    {
      threshold: 0.45,
      rootMargin: "0px 0px -10% 0px"
    }
  );

  vcGoldHeadings.forEach(function (heading) {
    vcGoldHeadingObserver.observe(heading);
  });
});


/* ==========================================================
   VothCapital Membership Page
   Monthly Briefing Rows – Paragraph Glow After Heading
   ========================================================== */

document.addEventListener("DOMContentLoaded", function () {
  const vcBriefingHeadings = document.querySelectorAll(
    "#shopify-section-template--29218499854713__image_with_text_TG6E78 .image-with-text__heading," +
    "#shopify-section-template--29218499854713__image_with_text_9kweqj .image-with-text__heading," +
    "#shopify-section-template--29218499854713__image_with_text_py6bhE .image-with-text__heading"
  );

  vcBriefingHeadings.forEach(function (heading) {
    heading.addEventListener(
      "animationend",
      function (event) {
        if (event.animationName !== "vcGoldBreathe") return;

        const contentBox = heading.closest(".image-with-text__content");
        const paragraph = contentBox
          ? contentBox.querySelector(".image-with-text__text")
          : null;

        if (!paragraph) return;

        setTimeout(function () {
          paragraph.classList.add("vc-blue-glow-active");
        }, 120);
      },
      { once: true }
    );
  });
});




/* ==========================================================
   VothCapital Membership Page
   Final CTA Heading – Falling Words + Golden Line Reveal
   ========================================================== */

document.addEventListener("DOMContentLoaded", function () {
  const vcFinaleHeading = document.querySelector(
    "#shopify-section-template--29218499854713__rich_text_xtVFWG .rich-text__heading"
  );

  if (!vcFinaleHeading) return;

  const vcFinaleWords = [
    {
      text: "Become",
      delay: "0.68s",
      distance: "-240px",
      rotation: "-8deg"
    },
    {
      text: "The",
      delay: "0.52s",
      distance: "-185px",
      rotation: "6deg"
    },
    {
      text: "Investor",
      delay: "0.22s",
      distance: "-225px",
      rotation: "-5deg"
    },
    {
      text: "Your",
      delay: "0.36s",
      distance: "-170px",
      rotation: "7deg"
    },
    {
      text: "Future",
      delay: "0s",
      distance: "-280px",
      rotation: "4deg"
    },
    {
      text: "Deserves",
      delay: "0.84s",
      distance: "-210px",
      rotation: "-6deg"
    }
  ];

  vcFinaleHeading.innerHTML = vcFinaleWords
    .map(function (word) {
      return `
        <span
          class="vc-finale-word"
          style="
            --vc-word-delay: ${word.delay};
            --vc-fall-distance: ${word.distance};
            --vc-fall-rotation: ${word.rotation};
          "
        >${word.text}</span>
      `;
    })
    .join("");

  const words = vcFinaleHeading.querySelectorAll(".vc-finale-word");
  let completedWords = 0;

  words.forEach(function (word) {
    word.addEventListener(
      "animationend",
      function (event) {
        if (event.animationName !== "vcFinaleWordFall") return;

        completedWords += 1;

        if (completedWords === words.length) {
          vcFinaleHeading.classList.add("vc-finale-complete");
        }
      },
      { once: true }
    );
  });

  const vcFinaleObserver = new IntersectionObserver(
    function (entries, observer) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        vcFinaleHeading.classList.add("vc-finale-active");
        setTimeout(function () {
  const vcFinaleParagraph = document.querySelector(
    "#shopify-section-template--29218499854713__rich_text_xtVFWG .rich-text__text"
  );

  if (vcFinaleParagraph) {
    vcFinaleParagraph.classList.add("vc-finale-paragraph-active");
  }
}, 900);
        observer.unobserve(vcFinaleHeading);
      });
    },
    {
      threshold: 0.45,
      rootMargin: "0px 0px -12% 0px"
    }
  );

  vcFinaleObserver.observe(vcFinaleHeading);
});



/* ==========================================================
   VothCapital – Money Playbook Elite
   Replace Shopify subscription frequency wording
   ========================================================== */

document.addEventListener('DOMContentLoaded', () => {
  const isEliteProduct =
    window.location.pathname === '/products/money-playbook-elite';

  if (!isEliteProduct) return;

  const replaceSubscriptionText = () => {
    const planText = document.querySelector(
      '.shopify_subscriptions_app_block .group_list_item span'
    );

    if (planText && planText.textContent.trim() === 'Deliver every month') {
      planText.textContent = 'Monthly Membership';
      return true;
    }

    return false;
  };

  // Try immediately in case the app block has already loaded.
  if (replaceSubscriptionText()) return;

  // Shopify's app block can load after the rest of the page,
  // so watch briefly until the text becomes available.
  const observer = new MutationObserver(() => {
    if (replaceSubscriptionText()) {
      observer.disconnect();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Stop observing after 10 seconds.
  setTimeout(() => observer.disconnect(), 10000);
});





/* ==========================================================
   MONEY PLAYBOOK ELITE — SMALL PHONES, Medium Phones, Large phones and tablets
   Animate final CTA (Button) only while directly visible
========================================================== */

document.addEventListener("DOMContentLoaded", function () {
  const finalEliteButton = document.querySelector(
    '#shopify-section-template--29218499854713__rich_text_FDRjxU .button'
  );

  if (!finalEliteButton) return;

  const smallPhoneQuery = window.matchMedia(
  "(min-width: 320px) and (max-width: 359px) and (orientation: portrait), " +
  "(min-width: 360px) and (max-width: 389px) and (orientation: portrait), " +
  "(min-width: 390px) and (max-width: 419px) and (orientation: portrait), " +
  "(min-width: 420px) and (max-width: 480px) and (orientation: portrait), " +
  "(min-width: 800px) and (max-width: 1200px) and (orientation: portrait), " +
  "(min-width: 540px) and (max-width: 639px) and (max-height: 430px) and (orientation: landscape), " +
  "(min-width: 640px) and (max-width: 800px) and (max-height: 420px) and (orientation: landscape), " +
  "(min-width: 700px) and (max-width: 800px) and (max-height: 500px) and (orientation: landscape), " +
  "(min-width: 801px) and (max-width: 1000px) and (max-height: 500px) and (orientation: landscape), " +
  "(min-width: 961px) and (max-width: 1400px) and (min-height: 501px) and (orientation: landscape)"
);

  const finalButtonObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!smallPhoneQuery.matches) {
          finalEliteButton.classList.remove("vc-mobile-stream-active");
          return;
        }

        if (entry.isIntersecting) {
          finalEliteButton.classList.add("vc-mobile-stream-active");
        } else {
          finalEliteButton.classList.remove("vc-mobile-stream-active");
        }
      });
    },
    {
      threshold: 0.75
    }
  );

  finalButtonObserver.observe(finalEliteButton);

  smallPhoneQuery.addEventListener("change", function (event) {
    if (!event.matches) {
      finalEliteButton.classList.remove("vc-mobile-stream-active");
    }
  });
});





/* ==========================================================
   MONEY PLAYBOOK ELITE — MOBILE AUTO HOVER
   Starts two seconds after the card becomes visible
========================================================== */

document.addEventListener("DOMContentLoaded", () => {
  const mobileQuery = window.matchMedia(
    "(max-width: 480px) and (orientation: portrait), " +
    "(max-width: 900px) and (max-height: 500px) and (orientation: landscape)"
  );

  if (!mobileQuery.matches) return;

  const eliteCard = document.querySelector(
    "#shopify-section-template--27169583628665__rich_text_wXhptN .rich-text__wrapper"
  );

  if (!eliteCard) return;

  let timer;

  const observer = new IntersectionObserver(
    ([entry]) => {
      clearTimeout(timer);

      if (entry.isIntersecting) {
        timer = setTimeout(() => {
          eliteCard.classList.add("vc-mobile-hover");
        }, 2000);
      } else {
        eliteCard.classList.remove("vc-mobile-hover");
      }
    },
    { threshold: 0.45 }
  );

  observer.observe(eliteCard);
});






/* ==========================================================
   HOMEPAGE HERO HEADING
   COMPLETE CINEMATIC REVEAL
========================================================== */

document.addEventListener("DOMContentLoaded", async () => {
  const heading = document.querySelector(
    "#shopify-section-template--27169583628665__image_banner_RVe3c8 .banner__heading"
  );

  if (!heading) return;
  if (heading.dataset.vcHeadingPrepared === "true") return;

  heading.dataset.vcHeadingPrepared = "true";

  const originalText = heading.textContent
    .replace(/\s+/g, " ")
    .trim();


  /* ========================================================
     CREATE THE ANIMATED TEXT STRUCTURE
  ======================================================== */

  heading.innerHTML = `
    <span
      class="vc-heading-intro vc-shine-word"
      data-text="Long-term investing"
    >Long-term investing</span><span class="vc-heading-rest"> made simple with <span class="vc-heading-ending"><span
          class="vc-gold-word vc-shine-word"
          data-text="structure"
        >structure</span> <span class="vc-white-word">and</span> <span
          class="vc-gold-word vc-shine-word"
          data-text="discipline"
        >discipline</span></span></span>
  `;

  heading.setAttribute("aria-label", originalText);
  heading.classList.add("vc-heading-ready");


  /* Wait for the correct font measurements */
  if (document.fonts?.ready) {
    await document.fonts.ready;
  }


  requestAnimationFrame(() => {
    const intro = heading.querySelector(".vc-heading-intro");

    if (!intro) return;

    const headingRect = heading.getBoundingClientRect();
    const introRect = intro.getBoundingClientRect();

    const headingCenter =
      headingRect.left + headingRect.width / 2;

    const introCenter =
      introRect.left + introRect.width / 2;

    const centerOffset =
      headingCenter - introCenter;

    intro.style.setProperty(
      "--vc-intro-center-offset",
      `${centerOffset}px`
    );


    requestAnimationFrame(() => {
      /* Begin the main entrance animation */
      heading.classList.add("vc-heading-play");


      /* After the complete heading is visible,
         turn structure and discipline gold */
      window.setTimeout(() => {
        heading
          .querySelectorAll(".vc-gold-word")
          .forEach((word) => {
            word.classList.add("vc-gold-active");
          });
      }, 3250);


      /* After the gold transition finishes,
         activate the slow repeating shine */
      window.setTimeout(() => {
        heading
          .querySelectorAll(".vc-shine-word")
          .forEach((word) => {
            word.classList.add("vc-shine-active");
          });
      }, 2000);



      /* ========================================================
   HOMEPAGE HERO PARAGRAPH
   Reveal after the heading has appeared
======================================================== */

const heroParagraph = document.querySelector(
  "#shopify-section-template--27169583628665__image_banner_RVe3c8 .banner__text p"
);


/* Paragraph begins appearing after the heading reveal */
window.setTimeout(() => {
  if (!heroParagraph) return;

  heroParagraph.classList.add("vc-paragraph-visible");
}, 3850);


/* Snow-white glow begins after the paragraph is visible */
window.setTimeout(() => {
  if (!heroParagraph) return;

  heroParagraph.classList.add("vc-paragraph-glow");
}, 5100);
    });
  });
});



























